import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Sun, Moon, AlertCircle, Loader2, CheckCircle2, ArrowLeft, Mail, Key, Lock, ShieldAlert } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EsqueciSenha = () => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  // Step: 'info' | 'code' | 'success'
  const [step, setStep] = useState('info');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Digite seu e-mail');
      return;
    }

    if (!code.trim()) {
      setError('Digite o código de segurança');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password-with-code`, {
        email: email.trim().toLowerCase(),
        code: code.trim().toUpperCase(),
        new_password: newPassword
      });
      setStep('success');
    } catch (err) {
      const message = err.response?.data?.detail || 'Erro ao redefinir senha';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      {/* Header */}
      <header className="p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src="/assets/logo-symbol.png" alt="LifePort" className="h-10 w-10 object-contain" />
          <div className="flex flex-col">
            <span className="font-bold text-xl font-['Manrope'] text-[hsl(var(--lifeport-red))]">LifePort</span>
            <span className="text-xs text-muted-foreground">Saúde no Lar</span>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={toggleTheme}>
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </header>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          {step === 'info' && (
            <>
              <CardHeader className="text-center space-y-2">
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                  <ShieldAlert className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-2xl font-['Manrope']">Redefinir Senha</CardTitle>
                <CardDescription>
                  Para sua segurança, a redefinição de senha requer um código de segurança
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                  <ShieldAlert className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 dark:text-amber-400 text-sm">
                    <strong>Como obter o código de segurança:</strong>
                    <ol className="list-decimal list-inside mt-2 space-y-1">
                      <li>Entre em contato com o administrador de segurança</li>
                      <li>Solicite um código de recuperação de senha</li>
                      <li>O código é válido por 15 minutos e uso único</li>
                    </ol>
                  </AlertDescription>
                </Alert>

                <Button 
                  className="w-full" 
                  onClick={() => setStep('code')}
                >
                  <Key className="mr-2 h-4 w-4" />
                  Já tenho um código
                </Button>

                <div className="text-center">
                  <Link 
                    to="/login" 
                    className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Voltar ao login
                  </Link>
                </div>
              </CardContent>
            </>
          )}

          {step === 'code' && (
            <>
              <CardHeader className="text-center space-y-2">
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
                  <Key className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-2xl font-['Manrope']">Redefinir senha com código</CardTitle>
                <CardDescription>
                  Insira seu e-mail, o código de segurança e crie uma nova senha
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail cadastrado</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="code">Código de segurança</Label>
                    <Input
                      id="code"
                      type="text"
                      placeholder="XXXXXXXX"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                      maxLength={8}
                      className="text-center text-xl tracking-widest font-mono uppercase"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Código de 8 caracteres fornecido pelo administrador
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Nova senha</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmar nova senha</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Repita a nova senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Redefinindo...
                      </>
                    ) : (
                      <>
                        <Lock className="mr-2 h-4 w-4" />
                        Redefinir senha
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <button 
                      type="button"
                      onClick={() => setStep('info')}
                      className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Voltar
                    </button>
                  </div>
                </form>
              </CardContent>
            </>
          )}

          {step === 'success' && (
            <>
              <CardHeader className="text-center space-y-2">
                <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-2">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
                <CardTitle className="text-2xl font-['Manrope']">Senha alterada!</CardTitle>
                <CardDescription>
                  Sua senha foi redefinida com sucesso
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700 dark:text-green-400">
                    Agora você pode fazer login com sua nova senha.
                  </AlertDescription>
                </Alert>

                <Button 
                  className="w-full" 
                  onClick={() => navigate('/login')}
                >
                  <Lock className="mr-2 h-4 w-4" />
                  Ir para o login
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Footer */}
      <footer className="p-4 text-center text-sm text-muted-foreground">
        © 2024 LifePort. Soluções em Saúde - Saúde no Lar.
      </footer>
    </div>
  );
};

export default EsqueciSenha;
